import { useRef, useCallback, useState, useEffect } from 'react'
import { bggHttp, hasBggApiToken } from '../bggHttp'
import { mergeCollectionResponses } from '../bggCollectionParse'

/**
 * Fetches one or more BGG owned collections with retry, stale-response guard, and optional cancel of scheduled retries.
 *
 * @param {{ maxRetry?: number }} [options]
 */
export function useBggOwnedCollectionFetch(options = {}) {
	const maxRetry = options.maxRetry ?? 5
	const fetchGenRef = useRef(0)
	const retryCountRef = useRef(0)
	const timeoutRef = useRef(null)
	const [loading, setLoading] = useState(false)

	const cancelScheduledRetry = useCallback(() => {
		if (timeoutRef.current != null) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}, [])

	useEffect(() => () => cancelScheduledRetry(), [cancelScheduledRetry])

	/**
	 * @param {{
	 *   getRequestConfig: () => { usernames: string[], params: object } | null | undefined
	 *   onBeforeAttempt?: () => void
	 *   onSuccess: (multiCollection: object[]) => void
	 *   onHardError?: () => void
	 *   onMissingToken?: () => void
	 * }} payload
	 */
	const startFetch = useCallback(
		(payload) => {
			const { getRequestConfig, onBeforeAttempt, onSuccess, onHardError, onMissingToken } = payload
			cancelScheduledRetry()

			if (!hasBggApiToken()) {
				if (typeof onMissingToken === 'function') {
					onMissingToken()
				}
				return
			}

			fetchGenRef.current += 1
			const generation = fetchGenRef.current
			retryCountRef.current = 0

			const attempt = () => {
				if (generation !== fetchGenRef.current) return

				const cfg = getRequestConfig()
				if (!cfg || !cfg.usernames || cfg.usernames.length === 0) {
					setLoading(false)
					return
				}

				if (typeof onBeforeAttempt === 'function') {
					onBeforeAttempt()
				}
				setLoading(true)

				const { usernames, params } = cfg
				const promises = usernames.map((user_name) =>
					bggHttp.get('/collection', {
						params: {
							username: user_name,
							...params,
						},
					})
				)

				Promise.all(promises)
					.then((responses) => {
						if (generation !== fetchGenRef.current) return
						retryCountRef.current = 0
						setLoading(false)
						const multiCollection = mergeCollectionResponses(responses)
						onSuccess(multiCollection)
					})
					.catch(() => {
						if (generation !== fetchGenRef.current) return
						retryCountRef.current += 1
						if (retryCountRef.current < maxRetry) {
							const delayMs = 1000 * retryCountRef.current
							timeoutRef.current = setTimeout(() => {
								timeoutRef.current = null
								attempt()
							}, delayMs)
						} else {
							retryCountRef.current = 0
							setLoading(false)
							if (typeof onHardError === 'function') {
								onHardError()
							}
						}
					})
			}

			attempt()
		},
		[maxRetry, cancelScheduledRetry]
	)

	return { startFetch, loading, cancelScheduledRetry }
}
